import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Typography,
  Card,
  Tag,
  Space,
  Alert,
  Tooltip,
  Switch,
  Select,
  Form,
  Input,
  message,
  Spin,
  Badge,
} from "antd";
import {
  LinkOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  WhatsAppOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { ReactFlowProvider } from "reactflow";
import api from "../../../services/api";
import WhatsAppFlowMappingFlow from "./WhatsAppFlowMappingFlow";
import CommonSelect from "../../../core/common/commonSelect";

const { Title, Text } = Typography;
const { Option } = Select;

interface WhatsAppMessage {
  id: number;
  phone_number_id: string;
  from: string;
  message_id: string;
  type: string;
  text_body: string | null;
  submitted_data: any;
  flow_id: string | null;
  flow_token: string | null;
  action: string | null;
  raw_payload: any;
  company_id: number;
  company_type: string;
  createdAt: string;
  updatedAt: string;
  waba_id: number;
}

interface Conversation {
  id: number;
  customer_name: string;
  phone: string;
  last_message: string;
  last_message_type: string;
  last_message_time: string;
  total_messages: number;
  unread_count: number;
  company_id: number;
  company_type: string;
  messages: WhatsAppMessage[];
  lead_status: string;
  lead_score: number;
  waba_id: number;
  phone_number_id: string;
}

interface CRMFormField {
  id: number | string;
  type: string;
  label: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface CRMForm {
  key: number;
  LeadFormName: string;
  CompanyName: string;
  CompanyId: number;
  CompanyType: string;
  structure: CRMFormField[];
  Pipeline: string;
  employees: number[];
  pipeline_id: number;
  deal_pipeline_id: number | null;
}

interface WhatsAppFlowMapping {
  id?: number;
  flow_id: string;
  crm_form_id: number;
  source_field: string;
  source_field_label?: string;
  target_field_id: string;
  target_field_label: string;
  mapping_type: "automatic" | "manual" | "keyword_based";
  is_active: boolean;
  keywords?: string[];
  mapping_order?: number;
  transformation_rule?: string;
  created_at?: string;
}

interface WhatsAppFlowMappingModalProps {
  visible: boolean;
  onCancel: () => void;
  conversation: Conversation | null;
  signature: string | null;
  onSuccess?: () => void;
}

const WhatsAppFlowMappingModal: React.FC<WhatsAppFlowMappingModalProps> = ({
  visible,
  onCancel,
  conversation,
  signature,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Data states
  const [crmForms, setCrmForms] = useState<CRMForm[]>([]);
  const [selectedCrmForm, setSelectedCrmForm] = useState<CRMForm | null>(null);
  const [fieldMappings, setFieldMappings] = useState<WhatsAppFlowMapping[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);

  // Flow detection states
  const [detectedFlows, setDetectedFlows] = useState<
    Array<{
      flow_id: string;
      flow_name: string;
      message_count: number;
      has_submitted_data: boolean;
      submitted_data_sample: any;
      sample_fields: string[];
    }>
  >([]);

  // UI states
  const [showFlowMapping, setShowFlowMapping] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<{
    type: "mapping" | "allMappings" | "flow";
    id?: number;
    flowId?: string;
  } | null>(null);

  // Fetch CRM forms
  const fetchCRMForms = async (): Promise<boolean> => {
    if (!conversation) return false;

    try {
      const response = await api.post("leadService/getLeadFormsByCompany", {
        signature,
        company_id: conversation.company_id,
        company_type: conversation.company_type,
      });

      if (response.data?.msg === "Data Found") {
        const forms = response.data?.data?.data || [];
        setCrmForms(forms);

        if (forms.length > 0 && !selectedCrmForm) {
          setSelectedCrmForm(forms[0]);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching CRM forms:", error);
      message.error("Error loading CRM forms");
      return false;
    }
  };

  // Detect flows from conversation
  const detectFlowsFromConversation = () => {
    if (!conversation) return;

    const flowsMap = new Map<
      string,
      {
        flow_id: string;
        flow_name: string;
        message_count: number;
        has_submitted_data: boolean;
        submitted_data_sample: any;
        sample_fields: string[];
      }
    >();

    // Group messages by flow_id
    conversation.messages.forEach((message) => {
      const flowId = message.flow_id || "no_flow";

      if (!flowsMap.has(flowId)) {
        flowsMap.set(flowId, {
          flow_id: flowId,
          flow_name:
            flowId === "no_flow"
              ? "General Messages"
              : `Flow: ${flowId.substring(0, 8)}...`,
          message_count: 0,
          has_submitted_data: false,
          submitted_data_sample: null,
          sample_fields: [],
        });
      }

      const flow = flowsMap.get(flowId)!;
      flow.message_count++;

      // Check for submitted data
      if (
        message.submitted_data &&
        Object.keys(message.submitted_data).length > 0
      ) {
        flow.has_submitted_data = true;

        // Store sample data
        if (!flow.submitted_data_sample) {
          flow.submitted_data_sample = message.submitted_data;
        }

        // Extract field names
        Object.keys(message.submitted_data).forEach((field) => {
          if (!flow.sample_fields.includes(field)) {
            flow.sample_fields.push(field);
          }
        });
      }

      // Extract fields from text body using keywords
      if (message.text_body) {
        const commonFields = [
          "name",
          "email",
          "phone",
          "mobile",
          "location",
          "city",
          "country",
          "company",
          "message",
          "query",
        ];
        commonFields.forEach((field) => {
          if (
            message.text_body?.toLowerCase().includes(field) &&
            !flow.sample_fields.includes(field)
          ) {
            flow.sample_fields.push(field);
          }
        });
      }
    });

    // Convert to array and sort by message count
    const flows = Array.from(flowsMap.values()).sort(
      (a, b) => b.message_count - a.message_count
    );

    setDetectedFlows(flows);

    // Auto-select first flow with submitted data, or first flow
    if (flows.length > 0) {
      const flowWithData = flows.find(
        (f) => f.has_submitted_data && f.sample_fields.length > 0
      );
      setSelectedFlowId(flowWithData?.flow_id || flows[0].flow_id);
    }
  };

  // Fetch existing mappings
  const fetchExistingMappings = async (flowId: string, crmFormId: number) => {
    try {
      const response = await api.post("leadService/getWhatsAppFlowMappings", {
        signature,
        flow_id: flowId,
        crm_form_id: crmFormId,
        company_id: conversation?.company_id,
        company_type: conversation?.company_type,
      });

      if (
        response.data?.message?.[0]?.status &&
        response.data.message[0].data
      ) {
        setFieldMappings(response.data.message[0].data);
      } else {
        setFieldMappings([]);
      }
    } catch (error) {
      console.error("Error fetching mappings:", error);
      setFieldMappings([]);
    }
  };

  // Initialize when modal opens
  useEffect(() => {
    if (visible && conversation) {
      setLoading(true);
      Promise.all([fetchCRMForms()])
        .then(() => {
          detectFlowsFromConversation();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [visible, conversation]);

  // Load mappings when flow or form changes
  useEffect(() => {
    if (selectedFlowId && selectedCrmForm && visible) {
      fetchExistingMappings(selectedFlowId, selectedCrmForm.key);
    }
  }, [selectedFlowId, selectedCrmForm, visible]);

  // Get available WhatsApp fields for selected flow
  const getAvailableWhatsAppFields = (): string[] => {
    if (!selectedFlowId || detectedFlows.length === 0) {
      return [];
    }

    const flow = detectedFlows.find((f) => f.flow_id === selectedFlowId);
    if (!flow) return [];

    // Combine submitted data fields and extracted fields
    const fields = new Set<string>();

    if (flow.submitted_data_sample) {
      Object.keys(flow.submitted_data_sample).forEach((key) => {
        fields.add(key);
      });
    }

    flow.sample_fields.forEach((field) => {
      fields.add(field);
    });

    return Array.from(fields).sort();
  };

  // Auto-map fields
  const autoMapFields = () => {
    if (!selectedCrmForm || !selectedFlowId) return;

    const whatsappFields = getAvailableWhatsAppFields();
    const newMappings: WhatsAppFlowMapping[] = [];

    whatsappFields.forEach((whatsappField) => {
      const suggestedCrmField = suggestCrmField(
        whatsappField,
        selectedCrmForm.structure
      );
      if (suggestedCrmField) {
        const existingMapping = fieldMappings.find(
          (m) => m.source_field === whatsappField
        );
        if (!existingMapping) {
          newMappings.push({
            source_field: whatsappField,
            target_field_id: suggestedCrmField.id.toString(),
            target_field_label: suggestedCrmField.label,
            crm_form_id: selectedCrmForm.key,
            flow_id: selectedFlowId,
            mapping_type: "automatic",
            is_active: true,
            source_field_label: whatsappField,
            keywords: [whatsappField.toLowerCase()],
          });
        }
      }
    });

    if (newMappings.length > 0) {
      setFieldMappings((prev) => [...prev, ...newMappings]);
      message.success(
        `Automatically mapped ${newMappings.length} fields to CRM form!`
      );
    } else {
      message.info("No new automatic mappings found.");
    }
  };

  // Suggest CRM field based on WhatsApp field name
  const suggestCrmField = (
    whatsappField: string,
    crmFields: CRMFormField[]
  ): CRMFormField | null => {
    const field = whatsappField.toLowerCase();

    const fieldMatchers = [
      {
        whatsapp: ["name", "full_name", "first_name", "last_name", "fullname"],
        crm: ["name", "full name", "fullname", "first name", "last name"],
      },
      { whatsapp: ["email", "email_address"], crm: ["email", "e-mail"] },
      {
        whatsapp: ["phone", "phone_number", "mobile", "contact", "whatsapp"],
        crm: ["phone", "mobile", "telephone", "contact", "whatsapp"],
      },
      { whatsapp: ["city", "location"], crm: ["city", "location"] },
      {
        whatsapp: ["company", "organization", "business"],
        crm: ["company", "organization", "business"],
      },
      {
        whatsapp: ["message", "query", "text"],
        crm: ["message", "query", "notes"],
      },
    ];

    for (const matcher of fieldMatchers) {
      if (matcher.whatsapp.some((f) => field.includes(f))) {
        const matchedField = crmFields.find((crmField) =>
          matcher.crm.some((crmPattern) =>
            crmField.label.toLowerCase().includes(crmPattern)
          )
        );
        if (matchedField) return matchedField;
      }
    }

    return null;
  };

  // Update field mapping
  const updateFieldMapping = async (
    mappingId: number,
    updateData: Partial<WhatsAppFlowMapping>
  ) => {
    try {
      const response = await api.post("leadService/updateWhatsAppFlowMapping", {
        signature,
        id: mappingId,
        ...updateData,
        company_id: conversation?.company_id,
        company_type: conversation?.company_type,
      });

      if (response.data?.message?.[0]?.status) {
        setFieldMappings((prev) =>
          prev.map((mapping) =>
            mapping.id === mappingId ? { ...mapping, ...updateData } : mapping
          )
        );
      } else {
        message.error(
          response.data?.message?.[0]?.msg || "Failed to update mapping"
        );
      }
    } catch (error: any) {
      console.error("Error updating field mapping:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg ||
        "Error updating field mapping";
      message.error(errorMessage);
    }
  };

  // Delete field mapping
  const deleteFieldMapping = async (mappingId: number) => {
    try {
      setIsDeleting(mappingId.toString());
      const response = await api.post("leadService/deleteWhatsAppFlowMapping", {
        signature,
        id: mappingId,
      });

      if (response.data?.message?.[0]?.status) {
        setFieldMappings((prev) =>
          prev.filter((mapping) => mapping.id !== mappingId)
        );
        message.success("Field mapping deleted successfully!");
      } else {
        message.error(
          response.data?.message?.[0]?.msg || "Failed to delete mapping"
        );
      }
    } catch (error: any) {
      console.error("Error deleting field mapping:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error deleting mapping";
      message.error(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  };

  // Delete all mappings for flow
  const deleteAllMappingsForFlow = async () => {
    if (!selectedFlowId || !selectedCrmForm) return;

    try {
      setIsDeleting("all");
      const response = await api.post(
        "leadService/deleteAllWhatsAppFlowMappings",
        {
          signature,
          flow_id: selectedFlowId,
          crm_form_id: selectedCrmForm.key,
          company_id: conversation?.company_id,
          company_type: conversation?.company_type,
        }
      );

      if (response.data?.message?.[0]?.status) {
        setFieldMappings([]);
        message.success("All field mappings deleted successfully!");
      } else {
        message.error(
          response.data?.message?.[0]?.msg || "Failed to delete mappings"
        );
      }
    } catch (error: any) {
      console.error("Error deleting all mappings:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error deleting mappings";
      message.error(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  };

  // Save all mappings
  const saveAllMappings = async () => {
    if (!selectedFlowId || !selectedCrmForm || !conversation) {
      message.error("Please select flow and CRM form");
      return;
    }

    if (fieldMappings.length === 0) {
      message.warning("No mappings to save");
      return;
    }

    try {
      setIsSaving(true);

      const payload = {
        signature,
        flow_id: selectedFlowId,
        crm_form_id: selectedCrmForm.key,
        crm_form_name: selectedCrmForm.LeadFormName,
        conversation_id: conversation.id,
        customer_phone: conversation.phone,
        company_id: conversation.company_id,
        company_type: conversation.company_type,
        mappings: fieldMappings.map((mapping) => ({
          source_field: mapping.source_field,
          source_field_label:
            mapping.source_field_label || mapping.source_field,
          target_field_id: mapping.target_field_id,
          target_field_label: mapping.target_field_label,
          mapping_type: mapping.mapping_type,
          is_active: mapping.is_active !== undefined ? mapping.is_active : true,
          keywords: mapping.keywords || [],
          mapping_order: mapping.mapping_order || 0,
          transformation_rule: mapping.transformation_rule || "",
        })),
      };

      const response = await api.post(
        "leadService/saveWhatsAppFlowMappings",
        payload
      );

      if (response.data?.message?.[0]?.status) {
        message.success(
          `Successfully saved ${fieldMappings.length} field mappings!`
        );

        if (onSuccess) {
          onSuccess();
        }

        // Optionally close the modal
        // onCancel();
      } else {
        message.error(
          response.data?.message?.[0]?.msg || "Failed to save mappings"
        );
      }
    } catch (error: any) {
      console.error("Error saving mappings:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg ||
        "Error saving field mappings";
      message.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle flow selection
  const handleFlowSelect = (flowId: string) => {
    setSelectedFlowId(flowId);
    setShowFlowMapping(true);
  };

  const renderDeleteModal = () => {
    if (!deleteRecord) return null;

    const getTitle = () => {
      switch (deleteRecord.type) {
        case "mapping":
          return "Delete Field Mapping";
        case "allMappings":
          return "Delete All Mappings";
        case "flow":
          return "Delete Flow Configuration";
        default:
          return "Delete Item";
      }
    };

    const getMessage = () => {
      switch (deleteRecord.type) {
        case "mapping":
          return "Are you sure you want to delete this field mapping? This action cannot be undone.";
        case "allMappings":
          return "Are you sure you want to delete ALL field mappings for this flow? This action cannot be undone and will remove all your mapping configurations.";
        case "flow":
          return "Are you sure you want to delete this flow configuration? This will remove all mappings for this flow.";
        default:
          return "Are you sure you want to delete this item?";
      }
    };

    const handleDelete = async () => {
      switch (deleteRecord.type) {
        case "mapping":
          if (deleteRecord.id) {
            await deleteFieldMapping(deleteRecord.id);
          }
          break;
        case "allMappings":
          await deleteAllMappingsForFlow();
          break;
      }
      setDeleteRecord(null);
    };

    return (
      <Modal
        title={getTitle()}
        open={!!deleteRecord}
        onOk={handleDelete}
        onCancel={() => setDeleteRecord(null)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        confirmLoading={isDeleting === (deleteRecord.id?.toString() || "all")}
      >
        <div className="text-center">
          <div className="mb-3">
            <span className="avatar avatar-xl bg-transparent-danger text-danger mb-3">
              <i className="ti ti-trash-x fs-36" />
            </span>
          </div>
          <p>{getMessage()}</p>
        </div>
      </Modal>
    );
  };
  const crmFormOptions = crmForms.map((form) => ({
    value: form.key.toString(),
    label: `${form.LeadFormName} (${form.Pipeline})`,
  }));
  return (
    <Modal
      title={
        <div>
          <Title
            level={4}
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <WhatsAppOutlined style={{ color: "#25D366" }} />
            WhatsApp Flow Field Mapping
          </Title>
          {conversation && (
            <Text type="secondary">
              Customer: {conversation.customer_name} • {conversation.phone}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      destroyOnClose
      className="whatsapp-flow-mapping-modal"
    >
      <Spin spinning={loading}>
        <div className="row">
          {/* Left Panel - Flow Selection */}
          <div className="col-md-4">
            <Card
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <span>Detected Flows</span>
                  <Badge
                    count={detectedFlows.length}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </div>
              }
              className="mb-3"
            >
              <div className="mb-3">
                <Text type="secondary">
                  Select a WhatsApp flow to map its fields to CRM form fields
                </Text>
              </div>

              <div
                className="flows-list"
                style={{ maxHeight: "400px", overflowY: "auto" }}
              >
                {detectedFlows.map((flow) => (
                  <Card
                    key={flow.flow_id}
                    size="small"
                    className={`mb-2 cursor-pointer ${
                      selectedFlowId == flow.flow_id ? "border-primary" : ""
                    }`}
                    onClick={() => handleFlowSelect(flow.flow_id)}
                    hoverable
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <Text strong>{flow.flow_name}</Text>
                        <div className="d-flex align-items-center mt-1">
                          <Tag color="blue" className="me-2">
                            {flow.message_count} msgs
                          </Tag>
                          {flow.has_submitted_data && (
                            <Tag color="green">
                              <FormOutlined /> Data
                            </Tag>
                          )}
                        </div>
                      </div>
                      {selectedFlowId == flow.flow_id && (
                        <Tag color="blue">Selected</Tag>
                      )}
                    </div>

                    {flow.sample_fields.length > 0 && (
                      <div className="mt-2">
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Fields: {flow.sample_fields.slice(0, 3).join(", ")}
                          {flow.sample_fields.length > 3 && "..."}
                        </Text>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Card>

            {/* CRM Form Selection */}
            <Card title="CRM Form Selection">
              <div className="mb-3">
                <label className="form-label">Select CRM Form</label>
                <CommonSelect
                  className="select"
                  options={crmFormOptions}
                  defaultValue={
                    selectedCrmForm
                      ? {
                          value: selectedCrmForm.key.toString(),
                          label: `${selectedCrmForm.LeadFormName} (${selectedCrmForm.Pipeline})`,
                        }
                      : { value: "", label: "Select CRM Form" }
                  }
                  onChange={(selectedOption: any) => {
                    if (selectedOption && selectedOption.value) {
                      const form = crmForms.find(
                        (f) => f.key.toString() === selectedOption.value
                      );
                      setSelectedCrmForm(form || null);
                    } else {
                      setSelectedCrmForm(null);
                    }
                  }}
                  placeHolder={"Select CRM Form"}
                />
              </div>

              {selectedCrmForm && (
                <div className="alert alert-info">
                  <div className="d-flex justify-content-between">
                    <div>
                      <Text strong>Selected:</Text>{" "}
                      {selectedCrmForm.LeadFormName}
                    </div>
                    <div>
                      <Text strong>Fields:</Text>{" "}
                      {selectedCrmForm.structure.length}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Right Panel - Flow Mapping */}
          <div className="col-md-8">
            {selectedFlowId && showFlowMapping ? (
              <Card
                title={
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span>Field Mapping</span>
                      <Tag color="blue" className="ms-2">
                        {
                          detectedFlows.find(
                            (f) => f.flow_id === selectedFlowId
                          )?.flow_name
                        }
                      </Tag>
                    </div>
                    <Space>
                      <Button
                        size="small"
                        icon={<SyncOutlined />}
                        onClick={autoMapFields}
                        disabled={!selectedCrmForm}
                      >
                        Auto-Map
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          setDeleteRecord({
                            type: "allMappings",
                            flowId: selectedFlowId,
                          })
                        }
                        disabled={fieldMappings.length === 0}
                      >
                        Delete All
                      </Button>
                    </Space>
                  </div>
                }
                className="h-100"
              >
                {/* React Flow Mapping Area */}
                {selectedCrmForm && (
                  <ReactFlowProvider>
                    <div style={{ height: "500px" }}>
                      <WhatsAppFlowMappingFlow
                        whatsappFields={getAvailableWhatsAppFields()}
                        crmFormFields={selectedCrmForm.structure}
                        existingMappings={fieldMappings}
                        onMappingsChange={setFieldMappings}
                        selectedFlowId={selectedFlowId}
                        selectedCrmForm={selectedCrmForm}
                      />
                    </div>
                  </ReactFlowProvider>
                )}

                {/* Current Mappings Summary */}
                {fieldMappings.length > 0 && (
                  <div className="mt-3">
                    <Card size="small">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Text strong>
                          Current Mappings ({fieldMappings.length})
                        </Text>
                        <Badge
                          count={fieldMappings.length}
                          style={{ backgroundColor: "#1890ff" }}
                        />
                      </div>
                      <div
                        className="table-responsive"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              <th>WhatsApp Field</th>
                              <th>→</th>
                              <th>CRM Field</th>
                              <th>Type</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fieldMappings.map((mapping, index) => (
                              <tr key={mapping.id || index}>
                                <td>
                                  <code>{mapping.source_field}</code>
                                  {mapping.keywords &&
                                    mapping.keywords.length > 0 && (
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: "11px" }}
                                      >
                                        Keywords:{" "}
                                        {mapping.keywords
                                          .slice(0, 2)
                                          .join(", ")}
                                        {mapping.keywords.length > 2 && "..."}
                                      </div>
                                    )}
                                </td>
                                <td className="text-center">→</td>
                                <td>
                                  <strong>{mapping.target_field_label}</strong>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    ID: {mapping.target_field_id}
                                  </div>
                                </td>
                                <td>
                                  <Tag
                                    color={
                                      mapping.mapping_type === "automatic"
                                        ? "success"
                                        : mapping.mapping_type ===
                                          "keyword_based"
                                        ? "blue"
                                        : "warning"
                                    }
                                  >
                                    {mapping.mapping_type}
                                  </Tag>
                                </td>
                                <td>
                                  <Switch
                                    size="small"
                                    checked={mapping.is_active}
                                    onChange={(checked) => {
                                      if (mapping.id) {
                                        updateFieldMapping(mapping.id, {
                                          is_active: checked,
                                        });
                                      } else {
                                        setFieldMappings((prev) =>
                                          prev.map((m, idx) =>
                                            idx === index
                                              ? { ...m, is_active: checked }
                                              : m
                                          )
                                        );
                                      }
                                    }}
                                  />
                                </td>
                                <td>
                                  <Space size="small">
                                    {!mapping.id && (
                                      <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() =>
                                          setFieldMappings((prev) =>
                                            prev.filter(
                                              (_, idx) => idx !== index
                                            )
                                          )
                                        }
                                      >
                                        Remove
                                      </Button>
                                    )}
                                    {mapping.id && (
                                      <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={
                                          isDeleting === mapping.id.toString()
                                        }
                                        onClick={() =>
                                          setDeleteRecord({
                                            type: "mapping",
                                            id: mapping.id,
                                          })
                                        }
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </Space>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </div>
                )}
              </Card>
            ) : (
              <Card className="h-100 d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <WhatsAppOutlined
                    style={{ fontSize: 48, color: "#ddd", marginBottom: 16 }}
                  />
                  <Title level={4} className="text-muted">
                    Select a WhatsApp Flow
                  </Title>
                  <Text type="secondary">
                    Choose a flow from the left panel to start mapping fields
                  </Text>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
          <div>
            <Text type="secondary">
              <WhatsAppOutlined /> WhatsApp Flow:{" "}
              {selectedFlowId || "None selected"}
            </Text>
          </div>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={saveAllMappings}
              loading={isSaving}
              disabled={!selectedFlowId || fieldMappings.length === 0}
            >
              Save {fieldMappings.length} Mapping
              {fieldMappings.length !== 1 ? "s" : ""}
            </Button>
          </Space>
        </div>

        {/* Stats */}
        <div className="row mt-2">
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <Text strong className="d-block">
                  WhatsApp Fields
                </Text>
                <Title level={3} className="text-primary">
                  {getAvailableWhatsAppFields().length}
                </Title>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <Text strong className="d-block">
                  CRM Fields
                </Text>
                <Title level={3} className="text-success">
                  {selectedCrmForm?.structure?.length || 0}
                </Title>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <Text strong className="d-block">
                  Mapped
                </Text>
                <Title level={3} className="text-warning">
                  {fieldMappings.length}
                </Title>
              </div>
            </div>
          </div>
        </div>
      </Spin>

      {/* Delete Confirmation Modal */}
      {renderDeleteModal()}
    </Modal>
  );
};

export default WhatsAppFlowMappingModal;
